import { MemberConsentLog, PendingNDAMember } from '@/models/member-status';
import { assertTokenValidForMutation } from '@/services/service-helpers';
import log from '@/lib/logger';

const API_BASE = `${import.meta.env.VITE_NBH_URL_ROOT}/api/members`;

export async function grantPrivacyConsent(token: string, memberId: string): Promise<MemberConsentLog | null> {
    assertTokenValidForMutation()
    try {
        const response = await fetch(`${API_BASE}/${memberId}/consent/privacy`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            log.error(`Failed to grant privacy consent: ${response.status}`);
            return null;
        }

        return await response.json();
    } catch (error) {
        log.error('Error granting privacy consent:', error);
        return null;
    }
}

export async function revokePrivacyConsent(token: string, memberId: string): Promise<MemberConsentLog | null> {
    assertTokenValidForMutation()
    try {
        const response = await fetch(`${API_BASE}/${memberId}/consent/privacy`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            log.error(`Failed to revoke privacy consent: ${response.status}`);
            return null;
        }

        return await response.json();
    } catch (error) {
        log.error('Error revoking privacy consent:', error);
        return null;
    }
}

export async function grantMediaConsent(token: string, memberId: string): Promise<MemberConsentLog | null> {
    assertTokenValidForMutation()
    try {
        const response = await fetch(`${API_BASE}/${memberId}/consent/media`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            log.error(`Failed to grant media consent: ${response.status}`);
            return null;
        }

        return await response.json();
    } catch (error) {
        log.error('Error granting media consent:', error);
        return null;
    }
}

export async function revokeMediaConsent(token: string, memberId: string): Promise<MemberConsentLog | null> {
    assertTokenValidForMutation()
    try {
        const response = await fetch(`${API_BASE}/${memberId}/consent/media`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            log.error(`Failed to revoke media consent: ${response.status}`);
            return null;
        }

        return await response.json();
    } catch (error) {
        log.error('Error revoking media consent:', error);
        return null;
    }
}

export async function getConsentHistory(token: string, memberId: string): Promise<MemberConsentLog[]> {
    try {
        const response = await fetch(`${API_BASE}/${memberId}/consent/history`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            log.error(`Failed to get consent history: ${response.status}`);
            return [];
        }

        return await response.json();
    } catch (error) {
        log.error('Error getting consent history:', error);
        return [];
    }
}

export async function signNDA(token: string, memberId: string): Promise<boolean> {
    assertTokenValidForMutation()
    try {
        const response = await fetch(`${API_BASE}/${memberId}/nda`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            log.error(`Failed to sign NDA: ${response.status}`);
            return false;
        }

        return true;
    } catch (error) {
        log.error('Error signing NDA:', error);
        return false;
    }
}

export async function getPendingNDAMembers(token: string): Promise<PendingNDAMember[]> {
    try {
        const response = await fetch(`${API_BASE}/pending-nda`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            log.error(`Failed to get pending NDA members: ${response.status}`);
            return [];
        }

        return await response.json();
    } catch (error) {
        log.error('Error getting pending NDA members:', error);
        return [];
    }
}

export async function getNDAReminderDays(token: string): Promise<number> {
    try {
        const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/nda/reminder-days`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            log.error(`Failed to get NDA reminder days: ${response.status}`);
            return 30; // Default fallback
        }

        const data = await response.json();
        return data.days;
    } catch (error) {
        log.error('Error getting NDA reminder days:', error);
        return 30; // Default fallback
    }
}

export async function updateNDAReminderDays(token: string, days: number): Promise<boolean> {
    assertTokenValidForMutation()
    try {
        const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/nda/reminder-days`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ days }),
        });

        if (!response.ok) {
            log.error(`Failed to update NDA reminder days: ${response.status}`);
            return false;
        }

        return true;
    } catch (error) {
        log.error('Error updating NDA reminder days:', error);
        return false;
    }
}